import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { get } from 'aws-amplify/api';
import LoadingSpinner from '../components/LoadingSpinner';
import PaginatedAuditLogList from '../components/PaginatedAuditLogList';
import { setDocumentTitle } from '../utils/titleManager';

function EventDetailScreen() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const shouldFetch = useRef(true);

  const fetchEvent = useCallback(async () => {
    try {
      const restOperation = get({
        apiName: 'events',
        path: `/events/${eventId}`,
        withCredentials: true
      });
      const { body } = await restOperation.response;
      const data = await body.json();
      setEvent(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (shouldFetch.current) {
      shouldFetch.current = false;
      fetchEvent();
    }
  }, [eventId, fetchEvent]);

  useEffect(() => {
    setDocumentTitle('Event Details', event?.name);
  }, [event]);

  if (loading) return <LoadingSpinner message="Loading event details..." />;
  if (error) return <div>Error: {error}</div>;
  if (!event) return <div>Event not found</div>;

  return (
    <div className="event-detail">
      <h2>{event.name}</h2>
      <div className="event-actions">
        <button onClick={() => navigate(`/events/${eventId}/edit`)}>
          Edit Event
        </button>
        <button onClick={() => navigate(`/guests/${eventId}`)}>
          Manage Guests
        </button>
        <button onClick={() => navigate(`/invitations/${eventId}`)}>
          Manage Invitations
        </button>
      </div>
      <div className="event-info">
        <div className="info-section">
          <h3>Basic Information</h3>
          <div className="info-item">
            <label>Name:</label>
            <p>{event.name}</p>
          </div>
          <div className="info-item">
            <label>Heading:</label>
            <p>{event.heading}</p>
          </div>
          <div className="info-item">
            <label>Date:</label>
            <p>{new Date(event.date).toLocaleDateString('en-US')}</p>
          </div>
          <div className="info-item">
            <label>Location:</label>
            <p>{event.location}</p>
          </div>
          <div className="info-item">
            <label>Description:</label>
            <p>{event.description}</p>
          </div>
          <div className="info-item">
            <label>Maximum Guests:</label>
            <p>{event.maxGuests || 'No limit'}</p>
          </div>
        </div>

        <div className="info-section">
          <h3>Event Details</h3>
          <div className="info-item">
            <label>Instructions:</label>
            <p>{event.instructions}</p>
          </div>
          <div className="info-item">
            <label>RSVP Details:</label>
            <p>{event.rsvpDetails}</p>
          </div>
          <div className="info-item">
            <label>Additional Information:</label>
            <p>{event.additionalInfo}</p>
          </div>
        </div>

        <div className="info-section">
          <h3>Styling</h3>
          <div className="info-item">
            <label>Custom CSS URL:</label>
            <p>{event.customCssUrl || 'None'}</p>
          </div>
        </div>
      </div>
      <div className="event-audit-logs">
        <h3>Event History</h3>
        <PaginatedAuditLogList
          resourceId={eventId}
          resourceType="event"
          pageSize={10}
        />
      </div>
    </div>
  );
}

export default EventDetailScreen;

